/**
 * 완료 조건 에디터
 */
import type { CompletionRules, LessonSequenceMode, CompletionRulesOverrides } from '@/types/configuration'

interface CompletionRulesEditorProps {
  rules: CompletionRules
  onUpdate: (rules: CompletionRules) => void
  overrides?: CompletionRulesOverrides
  onOverridesUpdate?: (overrides: CompletionRulesOverrides) => void
}

export const CompletionRulesEditor: React.FC<CompletionRulesEditorProps> = ({
  rules,
  onUpdate,
  overrides,
  onOverridesUpdate
}) => {
  // 기본값 처리
  const lessonSequenceMode = rules.lessonSequenceMode ?? 'sequential'
  const lessonSequenceOverridable = rules.lessonSequenceOverridable ?? true

  // 하위 변경 가능 토글 렌더링 헬퍼
  const renderOverridableToggle = (field: keyof CompletionRulesOverrides, label: string) => {
    if (!onOverridesUpdate) return null
    const isOverridable = overrides?.[field] !== false
    return (
      <label className="flex items-center gap-1.5 ml-auto cursor-pointer" title={isOverridable ? '하위에서 변경 가능' : '하위에서 변경 불가'}>
        <input
          type="checkbox"
          checked={isOverridable}
          onChange={(e) => onOverridesUpdate({ ...overrides, [field]: e.target.checked })}
          className="w-3 h-3 text-purple-600 rounded border-gray-300 focus:ring-purple-500"
        />
        <span className={`text-xs ${isOverridable ? 'text-purple-600' : 'text-gray-400'}`}>
          하위 변경
        </span>
      </label>
    )
  }

  return (
    <div className="space-y-6">
      {/* 레슨 간 순서 설정 */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-3">레슨 간 순서</h4>
        <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-100 rounded-lg p-4 space-y-4">
          <p className="text-xs text-purple-700">
            코스 내 레슨들의 진행 순서를 설정합니다.
          </p>
          
          {/* 순차/자유 선택 */}
          <div className="space-y-2">
            <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg hover:bg-white/50 transition-colors">
              <input
                type="radio"
                name="lessonSequence"
                checked={lessonSequenceMode === 'sequential'}
                onChange={() => onUpdate({ ...rules, lessonSequenceMode: 'sequential' })}
                className="w-4 h-4 mt-0.5 text-purple-600 border-gray-300 focus:ring-purple-500"
              />
              <div>
                <span className="text-sm font-medium text-gray-900">순차 진행</span>
                <p className="text-xs text-gray-500 mt-0.5">
                  이전 레슨을 완료해야 다음 레슨으로 진행할 수 있습니다. (1 → 2 → 3)
                </p>
              </div>
            </label>
            
            <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg hover:bg-white/50 transition-colors">
              <input
                type="radio"
                name="lessonSequence"
                checked={lessonSequenceMode === 'flexible'}
                onChange={() => onUpdate({ ...rules, lessonSequenceMode: 'flexible' })}
                className="w-4 h-4 mt-0.5 text-purple-600 border-gray-300 focus:ring-purple-500"
              />
              <div>
                <span className="text-sm font-medium text-gray-900">자유 진행</span>
                <p className="text-xs text-gray-500 mt-0.5">
                  학습자가 원하는 순서로 레슨을 자유롭게 선택할 수 있습니다.
                </p>
              </div>
            </label>
          </div>

          {/* 하위 레벨 변경 가능 여부 */}
          <div className="pt-3 border-t border-purple-200">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={lessonSequenceOverridable}
                onChange={(e) => onUpdate({ ...rules, lessonSequenceOverridable: e.target.checked })}
                className="w-4 h-4 mt-0.5 text-purple-600 rounded border-gray-300 focus:ring-purple-500"
              />
              <div>
                <span className="text-sm font-medium text-gray-900 flex items-center gap-2">
                  하위 레벨에서 변경 가능
                  <span className={`text-xs px-1.5 py-0.5 rounded ${lessonSequenceOverridable ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                    {lessonSequenceOverridable ? '선택' : '고정'}
                  </span>
                </span>
                <p className="text-xs text-gray-500 mt-0.5">
                  {lessonSequenceOverridable 
                    ? '클래스나 상품 레벨에서 이 설정을 변경할 수 있습니다.'
                    : '이 설정이 모든 하위 레벨에 고정 적용됩니다.'}
                </p>
              </div>
            </label>
          </div>
        </div>
      </div>

      {/* 레슨 내 완료 조건 */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-3">레슨 내 완료 조건</h4>
        <div className="space-y-4">
          {/* 모든 필수 컴포넌트 완료 */}
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={rules.allRequiredMustComplete}
              onChange={(e) => onUpdate({ ...rules, allRequiredMustComplete: e.target.checked })}
              className="w-4 h-4 mt-0.5 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
            />
            <div>
              <span className="text-sm font-medium text-gray-900 flex items-center gap-2">
                모든 필수 항목 완료 필요
                {renderOverridableToggle('allRequiredMustComplete', '모든 필수 항목')}
              </span>
              <p className="text-xs text-gray-500 mt-0.5">
                활성화하면 '필수'로 표시된 모든 컴포넌트를 완료해야 레슨이 완료됩니다.
              </p>
            </div>
          </label>

          {/* 순차적 진행 강제 (컴포넌트 간) */}
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={rules.enforceSequentialProgress}
              onChange={(e) => onUpdate({ ...rules, enforceSequentialProgress: e.target.checked })}
              className="w-4 h-4 mt-0.5 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
            />
            <div>
              <span className="text-sm font-medium text-gray-900 flex items-center gap-2">
                컴포넌트 순차 진행 강제
                {renderOverridableToggle('enforceSequentialProgress', '순차 진행')}
              </span>
              <p className="text-xs text-gray-500 mt-0.5">
                활성화하면 레슨 내에서 이전 컴포넌트(Pre-Quiz → Video → Post-Quiz)를 완료해야 다음으로 진행할 수 있습니다.
              </p>
            </div>
          </label>

          {/* 최소 점수 */}
          <div>
            <label className="flex items-center text-sm font-medium text-gray-900 mb-1">
              최소 통과 점수 (%)
              {renderOverridableToggle('minimumScore', '최소 점수')}
            </label>
            <div className="flex items-center gap-3">
              <input
                type="number"
                min={0}
                max={100}
                value={rules.minimumScore ?? ''}
                onChange={(e) => {
                  const value = e.target.value === '' ? undefined : parseInt(e.target.value)
                  onUpdate({ ...rules, minimumScore: value })
                }}
                placeholder="설정 안함"
                className="w-32 px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
              />
              <span className="text-xs text-gray-500">
                전체 퀴즈 평균 점수가 이 값 이상이어야 레슨 완료
              </span>
            </div>
            <p className="text-xs text-gray-400 mt-1">
              비워두면 점수와 관계없이 완료 처리됩니다.
            </p>
          </div>
        </div>
      </div>

      {/* 설명 박스 */}
      <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
        <h5 className="text-sm font-medium text-blue-800 mb-2">완료 조건 안내</h5>
        <ul className="text-xs text-blue-700 space-y-1">
          <li>• <strong>레슨 간 순서</strong>: 코스 내 여러 레슨들의 진행 순서를 제어합니다.</li>
          <li>• <strong>컴포넌트 순차 진행</strong>: 각 레슨 내의 Pre-Quiz, Video, Post-Quiz 순서를 제어합니다.</li>
          <li>• 최소 점수는 모든 퀴즈의 평균을 기준으로 계산됩니다.</li>
        </ul>
      </div>
    </div>
  )
}
